import { createAction, createReducer, isAsyncThunkAction, Middleware } from '@reduxjs/toolkit';

export const sessionChanged = createAction<string | null>('session/changed');
export const sessionReducer = createReducer({ identity: null as string | null, initialized: false }, builder => {
    builder.addCase(sessionChanged, (state, action) => {
        state.identity = action.payload;
        state.initialized = true;
    });
});

// Requests started by a previous account must not repopulate the reset store.
export const sessionRequestsMiddleware: Middleware = ({ getState }) => {
    const pending = new Set<string>();
    const stale = new Set<string>();
    const running = new Set<{ abort: (reason?: string) => void }>();
    return next => action => {
        if (typeof action === 'function') {
            const result = next(action);
            if (result && typeof result === 'object' && 'abort' in result && typeof result.abort === 'function'
                && 'then' in result && typeof result.then === 'function') {
                const request = result as { abort: (reason?: string) => void; then: (done: () => void, failed: () => void) => unknown };
                running.add(request);
                const clear = () => { running.delete(request); };
                request.then(clear, clear);
            }
            return result;
        }
        if (sessionChanged.match(action)) {
            const state = getState() as { session?: { initialized: boolean; identity: string | null } };
            if (!state.session?.initialized || state.session.identity !== action.payload) {
                pending.forEach(id => stale.add(id));
                pending.clear();
                running.forEach(request => request.abort('Account changed'));
                running.clear();
            }
        } else if (isAsyncThunkAction(action)) {
            const id = action.meta.requestId;
            if (action.type.endsWith('/pending')) pending.add(id);
            else {
                pending.delete(id);
                if (stale.delete(id)) return action;
            }
        }
        return next(action);
    };
};
